import { __assign, __read, __rest } from "tslib";
import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Tooltip, Text } from '@keen.io/ui-core';
import { useTooltip } from '@keen.io/react-hooks';
import { formatValue as valueFormatter, getFromPath, } from '@keen.io/charts-utils';
import { TooltipCategories } from './heatmap-chart.styles';
import { Heatmap, ShadowFilter } from './components';
import { ChartBase, Axes } from '../../components';
import { useDynamicChartLayout } from '../../hooks';
import { generateBlocks, getTooltipLabel } from './utils';
import { theme as defaultTheme } from '../../theme';
import { DEFAULT_MARGINS, MINIMAL_BLOCK_SIZE_IN_PIXELS, SEPARATOR, } from './constants';
var tooltipMotion = {
    transition: { duration: 0.3 },
    exit: { opacity: 0 },
};
export var HeatmapChart = function (_a) {
    var data = _a.data, svgDimensions = _a.svgDimensions, labelSelector = _a.labelSelector, _b = _a.theme, theme = _b === void 0 ? defaultTheme : _b, _c = _a.margins, margins = _c === void 0 ? DEFAULT_MARGINS : _c, _d = _a.minValue, minValue = _d === void 0 ? 'auto' : _d, _e = _a.maxValue, maxValue = _e === void 0 ? 'auto' : _e, _f = _a.keys, keys = _f === void 0 ? ['value'] : _f, _g = _a.layout, layout = _g === void 0 ? 'vertical' : _g, _h = _a.useDynamicLayout, useDynamicLayout = _h === void 0 ? true : _h, _j = _a.colorMode, colorMode = _j === void 0 ? 'continuous' : _j, _k = _a.steps, steps = _k === void 0 ? 2 : _k, _l = _a.xScaleSettings, xScaleSettings = _l === void 0 ? { type: 'band' } : _l, _m = _a.yScaleSettings, yScaleSettings = _m === void 0 ? { type: 'band' } : _m, _o = _a.padding, padding = _o === void 0 ? 2 : _o, range = _a.range, xAxisTitle = _a.xAxisTitle, yAxisTitle = _a.yAxisTitle, _p = _a.tooltipSettings, tooltipSettings = _p === void 0 ? {} : _p, onBlockSizeChange = _a.onBlockSizeChange;
    var _q = useDynamicChartLayout(useDynamicLayout, margins), layoutMargins = _q.layoutMargins, layoutReady = _q.layoutReady, setLayoutReady = _q.setLayoutReady, setLayoutMargins = _q.setLayoutMargins;
    var _r = generateBlocks({
        data: data,
        margins: layoutMargins,
        dimension: svgDimensions,
        labelSelector: labelSelector,
        keys: keys,
        minValue: minValue,
        maxValue: maxValue,
        layout: layout,
        colors: theme.colors,
        colorMode: colorMode,
        steps: steps,
        range: range,
        xAxisTitle: xAxisTitle,
        yAxisTitle: yAxisTitle,
        xScaleSettings: xScaleSettings,
        yScaleSettings: yScaleSettings,
    }), blocks = _r.blocks, xScale = _r.xScale, yScale = _r.yScale, _s = _r.settings, xTitle = _s.xAxisTitle, yTitle = _s.yAxisTitle, settings = __rest(_s, ["xAxisTitle", "yAxisTitle"]);
    var svgElement = useRef(null);
    var _t = useTooltip(svgElement), tooltipVisible = _t.tooltipVisible, tooltipPosition = _t.tooltipPosition, tooltipSelectors = _t.tooltipSelectors, tooltipMeta = _t.tooltipMeta, updateTooltipPosition = _t.updateTooltipPosition, hideTooltip = _t.hideTooltip;
    var tileSizeIsTooSmall = blocks.some(function (block) {
        return block.width < MINIMAL_BLOCK_SIZE_IN_PIXELS ||
            block.height < MINIMAL_BLOCK_SIZE_IN_PIXELS;
    });
    useEffect(function () {
        onBlockSizeChange(tileSizeIsTooSmall);
    }, [tileSizeIsTooSmall, onBlockSizeChange]);
    var themeTooltipSettings = theme.tooltip;
    return (React.createElement(React.Fragment, null,
        React.createElement(AnimatePresence, null, tooltipVisible && (React.createElement(motion.div, __assign({}, tooltipMotion, { initial: { opacity: 0, x: tooltipPosition.x, y: tooltipPosition.y }, animate: {
                x: tooltipPosition.x,
                y: tooltipPosition.y,
                opacity: 1,
            }, style: {
                position: 'absolute',
                pointerEvents: 'none',
            } }),
            React.createElement(Tooltip, { mode: themeTooltipSettings.mode, hasArrow: false },
                tooltipMeta && (React.createElement(TooltipCategories, null,
                    React.createElement(Text, __assign({}, themeTooltipSettings.labels.typography, { truncate: true }), tooltipMeta.xCategoryLabel + " " + SEPARATOR + " " + tooltipMeta.yCategoryLabel))),
                tooltipSelectors && (React.createElement(Text, __assign({}, themeTooltipSettings.values.typography), valueFormatter(getFromPath(data, tooltipSelectors[0].selector), tooltipSettings.formatValue))))))),
        React.createElement(ChartBase, { ref: svgElement, theme: theme, xScaleSettings: settings.xScaleSettings, yScaleSettings: settings.yScaleSettings, svgDimensions: svgDimensions, margins: layoutMargins },
            React.createElement(Axes, { svgElement: svgElement, layout: layout, useDynamicLayout: useDynamicLayout, initialMargins: margins, onComputeLayout: function (margins) {
                    setLayoutMargins(margins);
                    setLayoutReady(true);
                }, xScale: xScale, yScale: yScale, xTitle: xTitle, yTitle: yTitle }),
            layoutReady && (React.createElement(React.Fragment, null,
                React.createElement(ShadowFilter, null),
                React.createElement(Heatmap, { blocks: blocks, padding: padding, layout: layout, onMouseEnter: function (e, block) {
                        if (themeTooltipSettings.enabled) {
                            var _a = __read(block.coordinates, 2), x = _a[0], y = _a[1];
                            var metadata = {
                                xCategoryLabel: getTooltipLabel({
                                    scale: xScale,
                                    settings: settings.xScaleSettings,
                                    index: x,
                                }),
                                yCategoryLabel: getTooltipLabel({
                                    scale: yScale,
                                    settings: settings.yScaleSettings,
                                    index: y,
                                }),
                            };
                            updateTooltipPosition(e, [{ selector: block.selector, color: block.color }], metadata);
                        }
                    }, onMouseLeave: function () {
                        hideTooltip();
                    } }))))));
};
export default HeatmapChart;
